// =================================================================
// Professional report-style print helper (Completion Pass v3).
// Opens a clean print window with a branded header, optional meta
// strip, a tabular body, optional totals, and a footer. The original
// page DOM is never printed — this avoids the "screenshot of the
// dashboard" look the operator flagged.
//
// v3 — Tenant identity: when a `template` is passed (same shape used
// by printInvoice / printPosReceipt / printAccountStatement), the
// report header pulls business name, logo, address, phone, tax
// number, footer text, and primary color from that template. When no
// template is supplied, the header falls back to the iSaloon brand
// defaults — keeps backward compatibility with un-converted call
// sites.
// =================================================================

import { API_BASE } from "@/lib/api";

const DEFAULT_LOGO_URL = "https://customer-assets.emergentagent.com/job_beauty-hub-manage/artifacts/h3pmooxo_image.png";

const BRAND_DEFAULTS = {
  name_ar: "آي صالون",
  name_en: "iSaloon",
  tagline_ar: "نظام إدارة مراكز التجميل",
  tagline_en: "Beauty Centers Management System",
  burgundy: "#7A1F3D",
  ink: "#2B1C1C",
  muted: "#6b6b6b",
};

const escapeHtml = (s) => String(s ?? "")
  .replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;")
  .replaceAll("\"", "&quot;").replaceAll("'", "&#39;");

// Resolve a logo URL — accept absolute URLs as-is, otherwise prefix
// the API host (uploaded logos are stored under /uploads/...).
const resolveImg = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return API_BASE.replace(/\/api$/, "") + u;
};

const renderCell = (c) => {
  if (c && typeof c === "object" && !Array.isArray(c)) {
    const cls = c.className ? ` class="${escapeHtml(c.className)}"` : "";
    return `<td${cls}>${c.html ? (c.value ?? "") : escapeHtml(c.value)}</td>`;
  }
  return `<td>${escapeHtml(c)}</td>`;
};

/**
 * printReport
 * @param {Object} opts
 * @param {string} opts.title              Report title
 * @param {string} [opts.subtitle]         Smaller secondary line
 * @param {string} [opts.lang='ar']        'ar' | 'en'
 * @param {Array<{label,value}>} [opts.meta]
 * @param {string[]} opts.columns
 * @param {Array<Array<*>>} opts.rows
 * @param {Array<{label,value,className?}>} [opts.totals]
 * @param {string} [opts.footerNote]
 * @param {Object} [opts.template]         Tenant-saved print template
 *   - logo_url, business_name_ar, business_name_en
 *   - business_address_ar, business_address_en
 *   - phone, email, tax_number, cr_number
 *   - footer_text_ar, footer_text_en
 *   - primary_color
 */
export const printReport = ({
  title, subtitle, lang = "ar",
  meta = [], columns = [], rows = [], totals = [], footerNote,
  template = null,
}) => {
  const dir = lang === "ar" ? "rtl" : "ltr";
  const isAr = lang === "ar";
  const now = new Date();
  const ts = now.toLocaleString(isAr ? "ar-QA" : "en-GB", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });

  // Resolve tenant identity (falls back to iSaloon defaults when no
  // template is configured yet).
  const bizName = template
    ? (isAr ? (template.business_name_ar || template.business_name_en) : (template.business_name_en || template.business_name_ar))
    : (isAr ? BRAND_DEFAULTS.name_ar : BRAND_DEFAULTS.name_en);
  const bizTagline = template
    ? (isAr ? (template.business_address_ar || "") : (template.business_address_en || ""))
    : (isAr ? BRAND_DEFAULTS.tagline_ar : BRAND_DEFAULTS.tagline_en);
  const logoUrl = template?.logo_url ? resolveImg(template.logo_url) : DEFAULT_LOGO_URL;
  const accent = template?.primary_color || BRAND_DEFAULTS.burgundy;
  const phone = template?.phone || "";
  const email = template?.email || "";
  const taxNumber = template?.tax_number || "";
  const crNumber = template?.cr_number || "";
  const tenantFooter = template
    ? (isAr ? (template.footer_text_ar || "") : (template.footer_text_en || ""))
    : `${escapeHtml(bizName)} — ${escapeHtml(bizTagline)}`;

  const printedLabel = isAr ? "تاريخ الطباعة" : "Printed";
  const phoneLabel   = isAr ? "هاتف" : "Phone";
  const taxLabel     = isAr ? "الرقم الضريبي" : "VAT";
  const crLabel      = isAr ? "س.ت" : "CR";

  // Build the small contact strip under the business name (only the
  // pieces the tenant has filled in).
  const contactBits = [];
  if (phone)     contactBits.push(`${phoneLabel}: ${escapeHtml(phone)}`);
  if (email)     contactBits.push(escapeHtml(email));
  if (taxNumber) contactBits.push(`${taxLabel}: ${escapeHtml(taxNumber)}`);
  if (crNumber)  contactBits.push(`${crLabel}: ${escapeHtml(crNumber)}`);
  const contactHtml = contactBits.length
    ? `<p class="biz-contact">${contactBits.join(" &nbsp;·&nbsp; ")}</p>`
    : "";

  const metaHtml = meta.length
    ? `<div class="meta">${meta.map(m => `<div class="meta-item"><span class="meta-l">${escapeHtml(m.label)}</span><span class="meta-v">${escapeHtml(m.value ?? "—")}</span></div>`).join("")}</div>`
    : "";

  const totalsHtml = totals.length
    ? `<div class="totals">${totals.map(t => `<div class="total-item ${t.className || ""}"><span class="tl">${escapeHtml(t.label)}</span><span class="tv">${escapeHtml(t.value)}</span></div>`).join("")}</div>`
    : "";

  const html = `<!doctype html>
<html lang="${lang}" dir="${dir}">
<head>
<meta charset="utf-8"/>
<title>${escapeHtml(title)} — ${escapeHtml(bizName)}</title>
<style>
  @page { size: A4; margin: 14mm 12mm 18mm 12mm; }
  * { box-sizing: border-box; }
  html, body { font-family: ${isAr ? "'Tajawal','Cairo','Segoe UI',sans-serif" : "'Segoe UI','Helvetica Neue',Arial,sans-serif"}; color: ${BRAND_DEFAULTS.ink}; margin: 0; padding: 0; }
  body { font-size: 11px; line-height: 1.45; }
  .report-head { display: flex; align-items: flex-start; justify-content: space-between; border-bottom: 2px solid ${accent}; padding-bottom: 10px; margin-bottom: 14px; gap: 16px; }
  .brand { display: flex; align-items: center; gap: 12px; min-width: 0; flex: 1; }
  .brand img { width: 56px; height: 56px; object-fit: contain; border-radius: 6px; flex-shrink: 0; }
  .brand h1 { font-size: 18px; color: ${accent}; margin: 0; font-weight: 700; }
  .brand p { font-size: 10px; color: ${BRAND_DEFAULTS.muted}; margin: 2px 0 0 0; }
  .biz-contact { font-size: 9.5px; color: ${BRAND_DEFAULTS.muted}; margin: 4px 0 0 0 !important; }
  .head-right { text-align: ${isAr ? "left" : "right"}; flex-shrink: 0; }
  .head-right h2 { font-size: 14px; margin: 0; color: ${BRAND_DEFAULTS.ink}; }
  .head-right small { color: ${BRAND_DEFAULTS.muted}; font-size: 10px; }
  .meta { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 6px 14px; margin-bottom: 12px; background: #faf6f4; border: 1px solid #f0e6e3; padding: 8px 10px; border-radius: 6px; }
  .meta-item { display: flex; flex-direction: column; font-size: 10px; }
  .meta-l { color: ${BRAND_DEFAULTS.muted}; text-transform: uppercase; letter-spacing: .05em; font-size: 9px; }
  .meta-v { color: ${BRAND_DEFAULTS.ink}; font-weight: 600; font-size: 11px; margin-top: 2px; }
  table.rt { width: 100%; border-collapse: collapse; }
  table.rt thead th { background: ${accent}; color: #fff; font-weight: 600; text-align: ${isAr ? "right" : "left"}; padding: 7px 8px; font-size: 10.5px; letter-spacing: .02em; }
  table.rt tbody td { padding: 6px 8px; border-bottom: 1px solid #ececec; font-size: 11px; vertical-align: middle; }
  table.rt tbody tr:nth-child(even) td { background: #fbf9f9; }
  table.rt .num { font-variant-numeric: tabular-nums; }
  table.rt .pos { color: ${accent}; font-weight: 600; }
  table.rt .neg { color: #b91c1c; font-weight: 600; }
  table.rt .muted { color: ${BRAND_DEFAULTS.muted}; }
  table.rt .badge { display: inline-block; padding: 1px 6px; border-radius: 10px; font-size: 9px; font-weight: 600; }
  table.rt .badge-ok { background: #e8f4e8; color: #126326; }
  table.rt .badge-bad { background: #fdecec; color: #b91c1c; }
  table.rt .badge-warn { background: #fff4e0; color: #8a5a00; }
  .totals { display: flex; flex-wrap: wrap; gap: 8px 18px; justify-content: ${isAr ? "flex-start" : "flex-end"}; margin-top: 12px; border-top: 1px dashed #ccc; padding-top: 10px; }
  .total-item { display: flex; flex-direction: column; min-width: 110px; }
  .total-item .tl { font-size: 9px; color: ${BRAND_DEFAULTS.muted}; text-transform: uppercase; letter-spacing: .05em; }
  .total-item .tv { font-size: 14px; font-weight: 700; color: ${BRAND_DEFAULTS.ink}; }
  .total-item.gold .tv { color: ${accent}; }
  .total-item.rose .tv { color: #b91c1c; }
  .report-foot { position: fixed; bottom: 6mm; left: 0; right: 0; text-align: center; font-size: 9px; color: ${BRAND_DEFAULTS.muted}; padding: 0 12mm; }
  .empty { text-align: center; color: ${BRAND_DEFAULTS.muted}; padding: 16px 8px; }
  @media print {
    .no-screen { display: block !important; }
    .screen-only { display: none !important; }
  }
</style>
</head>
<body>
  <div class="report-head">
    <div class="brand">
      <img src="${escapeHtml(logoUrl)}" alt="logo"/>
      <div style="min-width:0;">
        <h1>${escapeHtml(bizName)}</h1>
        ${bizTagline ? `<p>${escapeHtml(bizTagline)}</p>` : ""}
        ${contactHtml}
      </div>
    </div>
    <div class="head-right">
      <h2>${escapeHtml(title)}</h2>
      ${subtitle ? `<small>${escapeHtml(subtitle)}</small><br/>` : ""}
      <small>${printedLabel}: <span class="num">${escapeHtml(ts)}</span></small>
    </div>
  </div>

  ${metaHtml}

  <table class="rt">
    <thead><tr>${columns.map(c => `<th>${escapeHtml(c)}</th>`).join("")}</tr></thead>
    <tbody>
      ${rows.length === 0
        ? `<tr><td colspan="${columns.length}" class="empty">${isAr ? "لا توجد بيانات" : "No data"}</td></tr>`
        : rows.map(r => `<tr>${r.map(renderCell).join("")}</tr>`).join("")}
    </tbody>
  </table>

  ${totalsHtml}

  <div class="report-foot">
    ${escapeHtml(tenantFooter)}
    ${footerNote ? ` · ${escapeHtml(footerNote)}` : ""}
  </div>

  <script>
    // Trigger native print once the logo + fonts have settled, then close.
    window.addEventListener('load', () => {
      setTimeout(() => { window.focus(); window.print(); }, 250);
    });
    window.addEventListener('afterprint', () => { setTimeout(() => window.close(), 200); });
  </script>
</body>
</html>`;

  const w = window.open("", "_blank", "width=900,height=900");
  if (!w) {
    alert(isAr ? "السماح بفتح النوافذ المنبثقة مطلوب للطباعة." : "Please allow pop-ups to print.");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
};
